import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Search, ArrowRight, Loader2, Dumbbell } from "lucide-react";
import { api } from "@/lib/api";
import { EXERCISE_FILTERS, DIFFICULTY_STYLES, matchesFilter } from "@/lib/exercises";

export default function Exercise() {
  const navigate = useNavigate();
  const [exercises, setExercises] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(EXERCISE_FILTERS[0]);

  useEffect(() => {
    api.get("/exercises").then((r) => setExercises(r.data.filter((e) => e.active !== false))).catch(() => setExercises([]));
  }, []);

  const visible = useMemo(() => {
    if (!exercises) return [];
    const q = query.trim().toLowerCase();
    return exercises.filter((ex) => matchesFilter(ex, filter) && (!q || ex.name.toLowerCase().includes(q) || ex.category.toLowerCase().includes(q) || (ex.tags || []).some((t) => t.toLowerCase().includes(q))));
  }, [exercises, query, filter]);

  return (
    <div className="animate-fade-in pb-4">
      <div className="px-5 pt-4 pb-3 flex items-center gap-3">
        <button
          data-testid="exercise-back-btn"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white dark:bg-emerald-900 border border-slate-100 dark:border-emerald-800 flex items-center justify-center text-slate-600 dark:text-slate-300"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <span className="text-[11px] font-semibold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">Move Daily</span>
          <h1 className="text-xl font-extrabold text-emerald-950 dark:text-emerald-50 font-head leading-tight">Exercise & Fitness</h1>
        </div>
        <span data-testid="exercise-count" className="text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-800 px-2.5 py-1 rounded-full">
          {exercises ? `${visible.length}/${exercises.length}` : "…"}
        </span>
      </div>

      <div className="px-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
          <input
            data-testid="exercise-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search exercises, conditions…"
            className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
          />
        </div>
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-none px-4 pb-1">
        {EXERCISE_FILTERS.map((f) => (
          <button
            key={f.label}
            data-testid={`exercise-filter-${f.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            onClick={() => setFilter(f)}
            className={`shrink-0 text-xs font-semibold px-3.5 py-2 rounded-full border transition-colors ${
              filter.label === f.label
                ? "bg-emerald-700 dark:bg-emerald-500 text-white dark:text-emerald-950 border-emerald-700 dark:border-emerald-500"
                : "bg-white dark:bg-emerald-900/60 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-emerald-800"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <p className="px-5 mt-3 text-[11px] text-slate-400 leading-relaxed">
        General educational fitness guidance only — not medical advice. Consult a qualified professional before starting a new routine.
      </p>

      {!exercises && (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      )}

      {exercises && visible.length === 0 && (
        <div className="px-6 py-14 text-center" data-testid="exercise-empty-state">
          <Dumbbell className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No exercises match your search.</p>
        </div>
      )}

      <div className="px-4 mt-3 space-y-3.5">
        {visible.map((ex) => (
          <ExerciseCard key={ex.id} ex={ex} onOpen={() => navigate(`/exercise/${ex.id}`)} />
        ))}
      </div>
    </div>
  );
}

const ExerciseCard = ({ ex, onOpen }) => (
  <div data-testid={`exercise-card-${ex.id}`} className="rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800 overflow-hidden shadow-sm">
    <div className="relative h-40 bg-slate-100 dark:bg-emerald-800 overflow-hidden">
      <img src={ex.image} alt={ex.name} className="w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute top-3 left-3 flex gap-1.5">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DIFFICULTY_STYLES[ex.difficulty] || DIFFICULTY_STYLES.Beginner}`}>{ex.difficulty}</span>
      </div>
      <div className="absolute bottom-3 left-4 right-4">
        <span className="text-[10px] font-semibold tracking-wider uppercase text-emerald-200">{ex.category}</span>
        <h3 className="text-base font-extrabold text-white font-head leading-tight">{ex.name}</h3>
      </div>
    </div>
    <div className="p-4">
      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-2">{ex.intro}</p>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {(ex.tags || []).slice(0, 3).map((t) => (
          <span key={t} className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-800 px-2 py-0.5 rounded-full">{t}</span>
        ))}
      </div>
      <button
        data-testid={`exercise-view-${ex.id}`}
        onClick={onOpen}
        className="mt-3.5 w-full flex items-center justify-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 dark:bg-emerald-600 dark:hover:bg-emerald-500 text-white text-xs font-bold py-2.5 rounded-xl transition-colors"
      >
        View Exercise <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);
